import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  FileSignature, Plus, Search, Eye, Edit, Trash2, 
  FileText, Calendar, Building, User, Euro,
  Clock, CheckCircle, XCircle, AlertCircle, Upload, Download,
  RefreshCw, Bell, Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  maintenance: 'Maintenance',
  assurance: 'Assurance',
  nettoyage: 'Nettoyage',
  gardiennage: 'Gardiennage',
  ascenseur: 'Ascenseur',
  chauffage: 'Chauffage',
  espaces_verts: 'Espaces verts',
  securite: 'Sécurité',
  autre: 'Autre'
};

const STATUS_CONFIG = {
  actif: { label: 'Actif', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  expire: { label: 'Expiré', color: 'bg-red-100 text-red-800', icon: XCircle },
  resilie: { label: 'Résilié', color: 'bg-gray-100 text-gray-800', icon: XCircle },
  en_attente: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock }
};

const ALERT_CONFIG = {
  expire: { color: 'bg-red-500', text: 'Expiré' },
  urgent: { color: 'bg-orange-500', text: 'Échéance proche' },
  attention: { color: 'bg-yellow-500', text: 'À surveiller' },
  ok: { color: 'bg-green-500', text: '' },
  none: { color: 'bg-gray-300', text: '' }
};

const Contrats = () => {
  const { user, token } = useAuth();
  const [contrats, setContrats] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBuilding, setFilterBuilding] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedContrat, setSelectedContrat] = useState(null);
  const [activeTab, setActiveTab] = useState('liste');

  const isAdmin = ['gestionnaire', 'admin', 'superadmin', 'directeur_cabinet', 'comptable', 'assistant'].includes(user?.role);

  const fetchContrats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterBuilding) params.append('building_id', filterBuilding);
      if (filterType) params.append('type_contrat', filterType);
      if (filterStatus) params.append('statut', filterStatus);
      
      const response = await fetch(`${API_URL}/api/contrats?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContrats(data.contrats || []);
      }
    } catch (error) {
      console.error('Error fetching contrats:', error);
    }
  }, [token, filterBuilding, filterType, filterStatus]);

  const fetchStats = useCallback(async () => {
    try {
      const params = filterBuilding ? `?building_id=${filterBuilding}` : '';
      const response = await fetch(`${API_URL}/api/contrats/stats${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token, filterBuilding]);

  const fetchAlerts = useCallback(async () => {
    try {
      const params = filterBuilding ? `?building_id=${filterBuilding}` : '';
      const response = await fetch(`${API_URL}/api/contrats/alertes${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAlerts(data);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, [token, filterBuilding]);

  const fetchBuildings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/buildings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBuildings(data);
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  }, [token]);

  useEffect(() => {
    Promise.all([fetchContrats(), fetchStats(), fetchAlerts(), fetchBuildings()])
      .finally(() => setLoading(false));
  }, [fetchContrats, fetchStats, fetchAlerts, fetchBuildings]);

  const handleDeleteContrat = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contrat ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/contrats/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Contrat supprimé');
        fetchContrats();
        fetchStats();
        fetchAlerts();
      } else {
        throw new Error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const filteredContrats = contrats.filter(c => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return c.titre?.toLowerCase().includes(search) ||
             c.prestataire_nom?.toLowerCase().includes(search) ||
             c.building_name?.toLowerCase().includes(search);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contrats-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Contrats</h1>
          <p className="text-gray-500 text-sm">Suivi des contrats fournisseurs et alertes de renouvellement</p>
        </div>
        {isAdmin && (
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-teal-500 hover:bg-teal-600"
            data-testid="create-contrat-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau contrat
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileSignature className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-gray-500">Total contrats</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.by_status?.actif || 0}</p>
                <p className="text-xs text-gray-500">Actifs</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Bell className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{alerts?.total_alertes || 0}</p>
                <p className="text-xs text-gray-500">Alertes</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Euro className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.montant_annuel_total?.toLocaleString('fr-FR')}€</p>
                <p className="text-xs text-gray-500">Coût annuel</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerts Banner */}
      {alerts?.total_alertes > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <Bell className="w-5 h-5 text-orange-600" />
            <h3 className="font-semibold text-orange-800">Alertes de renouvellement</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {alerts.alertes?.expire?.length > 0 && (
              <div className="bg-red-100 rounded-lg p-3">
                <p className="text-sm font-medium text-red-800">{alerts.alertes.expire.length} contrat(s) expiré(s)</p>
              </div>
            )}
            {alerts.alertes?.urgent?.length > 0 && (
              <div className="bg-orange-100 rounded-lg p-3">
                <p className="text-sm font-medium text-orange-800">{alerts.alertes.urgent.length} échéance(s) urgente(s)</p>
              </div>
            )}
            {alerts.alertes?.attention?.length > 0 && (
              <div className="bg-yellow-100 rounded-lg p-3">
                <p className="text-sm font-medium text-yellow-800">{alerts.alertes.attention.length} à surveiller</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="liste">Liste des contrats</TabsTrigger>
          <TabsTrigger value="alertes">Alertes ({alerts?.total_alertes || 0})</TabsTrigger>
          <TabsTrigger value="stats">Statistiques</TabsTrigger>
        </TabsList>

        <TabsContent value="liste" className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-contrats"
                />
              </div>
              <Select value={filterBuilding || "all"} onValueChange={(v) => setFilterBuilding(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-building">
                  <SelectValue placeholder="Tous les immeubles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les immeubles</SelectItem>
                  {buildings.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-type">
                  <SelectValue placeholder="Tous les types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {Object.entries(TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="Tous les statuts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Contrats List */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            {filteredContrats.length === 0 ? (
              <div className="text-center py-12">
                <FileSignature className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Aucun contrat trouvé</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredContrats.map(contrat => {
                  const statusConfig = STATUS_CONFIG[contrat.statut] || STATUS_CONFIG.actif;
                  const StatusIcon = statusConfig.icon;
                  const alertConfig = ALERT_CONFIG[contrat.alerte_renouvellement?.niveau] || ALERT_CONFIG.none;
                  
                  return (
                    <div 
                      key={contrat.id} 
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => { setSelectedContrat(contrat); setShowDetailModal(true); }}
                      data-testid={`contrat-item-${contrat.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900 truncate">{contrat.titre}</h3>
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                            {alertConfig.text && (
                              <Badge className={`${alertConfig.color} text-white`}>{alertConfig.text}</Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-3.5 h-3.5" />
                              {contrat.prestataire_nom}
                            </span>
                            <span className="flex items-center gap-1">
                              <Building className="w-3.5 h-3.5" />
                              {contrat.building_name}
                            </span>
                            <Badge variant="outline">{TYPE_LABELS[contrat.type_contrat]}</Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="text-gray-500">
                              <Calendar className="w-3.5 h-3.5 inline mr-1" />
                              {new Date(contrat.date_debut).toLocaleDateString('fr-FR')} - {new Date(contrat.date_fin).toLocaleDateString('fr-FR')}
                            </span>
                            {contrat.jours_avant_echeance !== null && (
                              <span className={contrat.jours_avant_echeance < 0 ? 'text-red-600' : contrat.jours_avant_echeance < 90 ? 'text-orange-600' : 'text-gray-500'}>
                                {contrat.jours_avant_echeance < 0 
                                  ? `Expiré depuis ${Math.abs(contrat.jours_avant_echeance)} jours`
                                  : `${contrat.jours_avant_echeance} jours restants`}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {contrat.montant_annuel && (
                            <span className="text-sm font-medium text-gray-700">
                              {contrat.montant_annuel.toLocaleString('fr-FR')}€/an
                            </span>
                          )}
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setSelectedContrat(contrat); setShowDetailModal(true); }}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="text-red-500 hover:text-red-700"
                              onClick={(e) => { e.stopPropagation(); handleDeleteContrat(contrat.id); }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="alertes" className="space-y-4">
          {alerts?.total_alertes === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center shadow-sm border">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-gray-600">Aucune alerte de renouvellement</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Expired */}
              {alerts?.alertes?.expire?.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                  <div className="bg-red-50 px-4 py-3 border-b">
                    <h3 className="font-semibold text-red-800 flex items-center gap-2">
                      <XCircle className="w-5 h-5" />
                      Contrats expirés ({alerts.alertes.expire.length})
                    </h3>
                  </div>
                  <div className="divide-y">
                    {alerts.alertes.expire.map(c => (
                      <ContratAlertItem key={c.id} contrat={c} onClick={() => { setSelectedContrat(c); setShowDetailModal(true); }} />
                    ))}
                  </div>
                </div>
              )}

              {/* Urgent */}
              {alerts?.alertes?.urgent?.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                  <div className="bg-orange-50 px-4 py-3 border-b">
                    <h3 className="font-semibold text-orange-800 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      Échéances urgentes ({alerts.alertes.urgent.length})
                    </h3>
                  </div>
                  <div className="divide-y">
                    {alerts.alertes.urgent.map(c => (
                      <ContratAlertItem key={c.id} contrat={c} onClick={() => { setSelectedContrat(c); setShowDetailModal(true); }} />
                    ))}
                  </div>
                </div>
              )}

              {/* Attention */}
              {alerts?.alertes?.attention?.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                  <div className="bg-yellow-50 px-4 py-3 border-b">
                    <h3 className="font-semibold text-yellow-800 flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      À surveiller ({alerts.alertes.attention.length})
                    </h3>
                  </div>
                  <div className="divide-y">
                    {alerts.alertes.attention.map(c => (
                      <ContratAlertItem key={c.id} contrat={c} onClick={() => { setSelectedContrat(c); setShowDetailModal(true); }} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="stats">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* By Type */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="font-semibold mb-4">Par type de contrat</h3>
              <div className="space-y-3">
                {Object.entries(stats?.by_type || {}).map(([type, data]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{TYPE_LABELS[type] || type}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium">{data.count} contrat(s)</span>
                      <span className="text-sm text-purple-600">{(data.montant || 0).toLocaleString('fr-FR')}€</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* By Status */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="font-semibold mb-4">Par statut</h3>
              <div className="space-y-3">
                {Object.entries(stats?.by_status || {}).map(([status, count]) => {
                  const config = STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100' };
                  return (
                    <div key={status} className="flex items-center justify-between">
                      <Badge className={config.color}>{config.label}</Badge>
                      <span className="font-medium">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Modal */}
      <CreateContratModal 
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        buildings={buildings}
        token={token}
        onSuccess={() => { fetchContrats(); fetchStats(); fetchAlerts(); setShowCreateModal(false); }}
      />

      {/* Detail Modal */}
      {selectedContrat && (
        <ContratDetailModal
          open={showDetailModal}
          onClose={() => { setShowDetailModal(false); setSelectedContrat(null); }}
          contrat={selectedContrat}
          token={token}
          isAdmin={isAdmin}
          onUpdate={() => { fetchContrats(); fetchStats(); fetchAlerts(); }}
        />
      )}
    </div>
  );
};

// Alert Item Component
const ContratAlertItem = ({ contrat, onClick }) => (
  <div className="p-4 hover:bg-gray-50 cursor-pointer" onClick={onClick}>
    <div className="flex items-center justify-between">
      <div>
        <p className="font-medium">{contrat.titre}</p>
        <p className="text-sm text-gray-500">{contrat.prestataire_nom} - {contrat.building_name}</p>
      </div>
      <div className="text-right">
        <p className="text-sm font-medium">{new Date(contrat.date_fin).toLocaleDateString('fr-FR')}</p>
        <p className="text-xs text-gray-500">{contrat.alerte_renouvellement?.message}</p>
      </div>
    </div>
  </div>
);

// Create Contrat Modal Component
const CreateContratModal = ({ open, onClose, buildings, token, onSuccess }) => {
  const [form, setForm] = useState({
    titre: '',
    building_id: '',
    prestataire_nom: '',
    type_contrat: '',
    description: '',
    montant_annuel: '',
    date_debut: new Date().toISOString().split('T')[0],
    date_fin: '',
    tacite_reconduction: true,
    preavis_jours: 90,
    conditions_resiliation: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contrats`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...form,
          montant_annuel: form.montant_annuel ? parseFloat(form.montant_annuel) : null,
          preavis_jours: parseInt(form.preavis_jours)
        })
      });

      if (response.ok) {
        toast.success('Contrat créé avec succès');
        onSuccess();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nouveau contrat fournisseur</DialogTitle>
          <DialogDescription>Renseignez les informations du contrat</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm font-medium">Titre du contrat *</label>
              <Input
                value={form.titre}
                onChange={(e) => setForm({...form, titre: e.target.value})}
                required
                placeholder="Ex: Contrat maintenance ascenseur"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Immeuble *</label>
              <Select value={form.building_id} onValueChange={(v) => setForm({...form, building_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {buildings.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Prestataire *</label>
              <Input
                value={form.prestataire_nom}
                onChange={(e) => setForm({...form, prestataire_nom: e.target.value})}
                required
                placeholder="Nom du prestataire"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Type de contrat *</label>
              <Select value={form.type_contrat} onValueChange={(v) => setForm({...form, type_contrat: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Montant annuel (€)</label>
              <Input
                type="number"
                value={form.montant_annuel}
                onChange={(e) => setForm({...form, montant_annuel: e.target.value})}
                placeholder="Montant HT"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Date de début *</label>
              <Input
                type="date"
                value={form.date_debut}
                onChange={(e) => setForm({...form, date_debut: e.target.value})}
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium">Date de fin *</label>
              <Input
                type="date"
                value={form.date_fin}
                onChange={(e) => setForm({...form, date_fin: e.target.value})}
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium">Préavis de résiliation (jours)</label>
              <Input
                type="number"
                value={form.preavis_jours}
                onChange={(e) => setForm({...form, preavis_jours: e.target.value})}
                placeholder="90"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.tacite_reconduction}
                onChange={(e) => setForm({...form, tacite_reconduction: e.target.checked})}
                className="rounded border-gray-300"
              />
              <label className="text-sm">Tacite reconduction</label>
            </div>

            <div className="col-span-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                rows={3}
                placeholder="Description du contrat..."
              />
            </div>

            <div className="col-span-2">
              <label className="text-sm font-medium">Conditions de résiliation</label>
              <Textarea
                value={form.conditions_resiliation}
                onChange={(e) => setForm({...form, conditions_resiliation: e.target.value})}
                rows={2}
                placeholder="Conditions particulières..."
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={loading} className="bg-teal-500 hover:bg-teal-600">
              {loading ? 'Création...' : 'Créer le contrat'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Contrat Detail Modal Component
const ContratDetailModal = ({ open, onClose, contrat, token, isAdmin, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('infos');
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(contrat);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setForm(contrat);
  }, [contrat]);

  const statusConfig = STATUS_CONFIG[contrat.statut] || STATUS_CONFIG.actif;
  const alertConfig = ALERT_CONFIG[contrat.alerte_renouvellement?.niveau] || ALERT_CONFIG.none;

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contrats/${contrat.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        toast.success('Contrat mis à jour');
        setEditMode(false);
        onUpdate();
      } else {
        throw new Error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTerminate = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir résilier ce contrat ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/contrats/${contrat.id}/resilier`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Contrat résilié');
        onUpdate();
        onClose();
      }
    } catch (error) {
      toast.error('Erreur lors de la résiliation');
    }
  };

  const handleRenew = async (nouvelleDateFin, nouveauMontant) => {
    try {
      const params = new URLSearchParams({ nouvelle_date_fin: nouvelleDateFin });
      if (nouveauMontant) params.append('nouveau_montant', nouveauMontant);
      
      const response = await fetch(`${API_URL}/api/contrats/${contrat.id}/renouveler?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Contrat renouvelé');
        setShowRenewModal(false);
        onUpdate();
      }
    } catch (error) {
      toast.error('Erreur lors du renouvellement');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/contrats/${contrat.id}/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        toast.success('Document ajouté');
        onUpdate();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                {contrat.titre}
                <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                {alertConfig.text && (
                  <Badge className={`${alertConfig.color} text-white`}>{alertConfig.text}</Badge>
                )}
              </DialogTitle>
              <div className="flex gap-2">
                {isAdmin && contrat.statut === 'actif' && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setShowRenewModal(true)}>
                      <RefreshCw className="w-4 h-4 mr-1" /> Renouveler
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600" onClick={handleTerminate}>
                      <XCircle className="w-4 h-4 mr-1" /> Résilier
                    </Button>
                  </>
                )}
                {isAdmin && !editMode && (
                  <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                    <Edit className="w-4 h-4 mr-1" /> Modifier
                  </Button>
                )}
              </div>
            </div>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full">
              <TabsTrigger value="infos" className="flex-1">Informations</TabsTrigger>
              <TabsTrigger value="documents" className="flex-1">Documents ({contrat.documents?.length || 0})</TabsTrigger>
              <TabsTrigger value="historique" className="flex-1">Historique</TabsTrigger>
            </TabsList>

            <TabsContent value="infos" className="space-y-4">
              {editMode ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Statut</label>
                      <Select value={form.statut} onValueChange={(v) => setForm({...form, statut: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Montant annuel (€)</label>
                      <Input
                        type="number"
                        value={form.montant_annuel || ''}
                        onChange={(e) => setForm({...form, montant_annuel: parseFloat(e.target.value) || null})}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Préavis (jours)</label>
                      <Input
                        type="number"
                        value={form.preavis_jours || ''}
                        onChange={(e) => setForm({...form, preavis_jours: parseInt(e.target.value) || 90})}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={form.tacite_reconduction}
                        onChange={(e) => setForm({...form, tacite_reconduction: e.target.checked})}
                        className="rounded border-gray-300"
                      />
                      <label className="text-sm">Tacite reconduction</label>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Notes</label>
                    <Textarea
                      value={form.notes || ''}
                      onChange={(e) => setForm({...form, notes: e.target.value})}
                      rows={3}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setEditMode(false)}>Annuler</Button>
                    <Button onClick={handleUpdate} disabled={loading} className="bg-teal-500 hover:bg-teal-600">
                      {loading ? 'Sauvegarde...' : 'Enregistrer'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-500">Prestataire</span>
                      <p className="font-medium flex items-center gap-1">
                        <Briefcase className="w-4 h-4" />
                        {contrat.prestataire_nom}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Immeuble</span>
                      <p className="font-medium flex items-center gap-1">
                        <Building className="w-4 h-4" />
                        {contrat.building_name}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Type</span>
                      <p className="font-medium">{TYPE_LABELS[contrat.type_contrat]}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Montant annuel</span>
                      <p className="font-medium text-purple-600">{contrat.montant_annuel?.toLocaleString('fr-FR')}€</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Période</span>
                      <p className="font-medium">
                        {new Date(contrat.date_debut).toLocaleDateString('fr-FR')} - {new Date(contrat.date_fin).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Échéance</span>
                      <p className={`font-medium ${contrat.jours_avant_echeance < 0 ? 'text-red-600' : contrat.jours_avant_echeance < 90 ? 'text-orange-600' : 'text-green-600'}`}>
                        {contrat.jours_avant_echeance < 0 
                          ? `Expiré depuis ${Math.abs(contrat.jours_avant_echeance)} jours`
                          : `${contrat.jours_avant_echeance} jours restants`}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Préavis</span>
                      <p className="font-medium">{contrat.preavis_jours} jours</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Tacite reconduction</span>
                      <p className="font-medium">{contrat.tacite_reconduction ? 'Oui' : 'Non'}</p>
                    </div>
                  </div>
                  
                  {contrat.description && (
                    <div>
                      <span className="text-sm text-gray-500">Description</span>
                      <p className="mt-1 text-gray-700">{contrat.description}</p>
                    </div>
                  )}

                  {contrat.conditions_resiliation && (
                    <div>
                      <span className="text-sm text-gray-500">Conditions de résiliation</span>
                      <p className="mt-1 text-gray-700">{contrat.conditions_resiliation}</p>
                    </div>
                  )}

                  {contrat.notes && (
                    <div>
                      <span className="text-sm text-gray-500">Notes</span>
                      <p className="mt-1 text-gray-700">{contrat.notes}</p>
                    </div>
                  )}

                  <div className="pt-4 border-t text-xs text-gray-400">
                    <p>Créé le {new Date(contrat.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="documents" className="space-y-4">
              {isAdmin && (
                <div className="flex justify-end">
                  <label className="cursor-pointer">
                    <input type="file" className="hidden" onChange={handleFileUpload} />
                    <Button variant="outline" size="sm" asChild>
                      <span><Upload className="w-4 h-4 mr-1" /> Ajouter un document</span>
                    </Button>
                  </label>
                </div>
              )}
              
              {contrat.documents?.length > 0 ? (
                <div className="space-y-2">
                  {contrat.documents.map((doc, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-sm">{doc.filename}</p>
                          <p className="text-xs text-gray-500">{new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}</p>
                        </div>
                      </div>
                      <a href={`${API_URL}${doc.url}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm"><Download className="w-4 h-4" /></Button>
                      </a>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">Aucun document</p>
              )}
            </TabsContent>

            <TabsContent value="historique" className="space-y-4">
              {contrat.historique?.length > 0 ? (
                <div className="space-y-3">
                  {contrat.historique.slice().reverse().map((h, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="p-1.5 bg-white rounded-full">
                        {h.action === 'creation' && <Plus className="w-4 h-4 text-green-600" />}
                        {h.action === 'modification' && <Edit className="w-4 h-4 text-blue-600" />}
                        {h.action === 'renouvellement' && <RefreshCw className="w-4 h-4 text-purple-600" />}
                        {h.action === 'resiliation' && <XCircle className="w-4 h-4 text-red-600" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{h.description}</p>
                        <p className="text-xs text-gray-500">
                          {h.user_name} - {new Date(h.date).toLocaleString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">Aucun historique</p>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Renew Modal */}
      <RenewContratModal
        open={showRenewModal}
        onClose={() => setShowRenewModal(false)}
        contrat={contrat}
        onRenew={handleRenew}
      />
    </>
  );
};

// Renew Contrat Modal
const RenewContratModal = ({ open, onClose, contrat, onRenew }) => {
  const [nouvelleDateFin, setNouvelleDateFin] = useState('');
  const [nouveauMontant, setNouveauMontant] = useState('');

  useEffect(() => {
    if (open && contrat?.date_fin) {
      // Default to 1 year after current end date
      const endDate = new Date(contrat.date_fin);
      endDate.setFullYear(endDate.getFullYear() + 1);
      setNouvelleDateFin(endDate.toISOString().split('T')[0]);
      setNouveauMontant(contrat.montant_annuel?.toString() || '');
    }
  }, [open, contrat]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Renouveler le contrat</DialogTitle>
          <DialogDescription>Définissez les nouvelles conditions du contrat</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Nouvelle date de fin *</label>
            <Input
              type="date"
              value={nouvelleDateFin}
              onChange={(e) => setNouvelleDateFin(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium">Nouveau montant annuel (€)</label>
            <Input
              type="number"
              value={nouveauMontant}
              onChange={(e) => setNouveauMontant(e.target.value)}
              placeholder="Laisser vide pour conserver le montant actuel"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button 
              onClick={() => onRenew(nouvelleDateFin, nouveauMontant ? parseFloat(nouveauMontant) : null)}
              disabled={!nouvelleDateFin}
              className="bg-teal-500 hover:bg-teal-600"
            >
              Renouveler
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Contrats;
